import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { usePageMeta } from "@/lib/meta";
import { Overline } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const client = axios.create({ baseURL: API, withCredentials: true });

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

function Label({ children }) {
  return <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8A8D94]">{children}</span>;
}

function InquiryCard({ i }) {
  return (
    <article data-testid={`inquiry-card-${i.id}`} className="border border-[#26282E]/10 bg-white p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="font-serif text-xl text-[#26282E]">{i.name}</h3>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#8A8D94]">{fmt(i.created_at)}</span>
      </div>
      <p className="mt-1 text-sm text-[#5B5E66]">
        {i.email}
        {i.phone ? ` · ${i.phone}` : ""} · {i.role_org}
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        {[i.service_interest, i.situation, i.format].filter(Boolean).map((t) => (
          <span key={t} className="border border-[#2F5D8C]/30 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-[#2F5D8C]">
            {t.replace(/-/g, " ")}
          </span>
        ))}
      </div>
      <div className="mt-4">
        <Label>Communication challenge</Label>
        <p className="mt-1 text-sm leading-relaxed text-[#26282E]/85">{i.challenge}</p>
      </div>
    </article>
  );
}

function AssessmentCard({ a }) {
  const [open, setOpen] = useState(false);
  return (
    <article data-testid={`assessment-card-${a.id}`} className="border border-[#26282E]/10 bg-white p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="font-serif text-xl text-[#26282E]">{a.name}</h3>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#8A8D94]">{fmt(a.created_at)}</span>
      </div>
      <p className="mt-1 text-sm text-[#5B5E66]">
        {a.email} · {a.role}
        {a.organization ? ` · ${a.organization}` : ""}
      </p>
      <p className="mt-3 text-sm text-[#26282E]/85">
        Self-assessment average: <span className="font-mono text-[#2F5D8C]">{a.average?.toFixed(2)} / 5.00</span>
      </p>
      <div className="mt-3">
        <Label>Main challenge</Label>
        <p className="mt-1 text-sm leading-relaxed text-[#26282E]/85">{a.challenge}</p>
      </div>
      <button
        data-testid={`assessment-toggle-${a.id}`}
        onClick={() => setOpen((v) => !v)}
        className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#2F5D8C] hover:text-[#254A6E]"
      >
        {open ? "Hide responses" : "View responses"}
      </button>
      {open && (
        <ul className="mt-4 space-y-2 border-t border-[#26282E]/10 pt-4">
          {a.answers.map((ans, j) => (
            <li key={j} className="flex items-start justify-between gap-4 text-xs text-[#5B5E66]">
              <span>{ans.question}</span>
              <span className="font-mono text-[#2F5D8C]">{ans.score}/5</span>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

export default function AdminDashboard() {
  usePageMeta("Inquiries | Credible Presence Admin", "Private administration area.");
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("inquiries");
  const [inquiries, setInquiries] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get("/auth/me");
        setMe(data);
        const [inq, asm] = await Promise.all([client.get("/admin/inquiries"), client.get("/admin/assessments")]);
        setInquiries(inq.data);
        setAssessments(asm.data);
      } catch {
        navigate("/admin");
      }
      setLoading(false);
    })();
  }, [navigate]);

  const logout = async () => {
    await client.post("/auth/logout").catch(() => {});
    navigate("/admin");
  };

  if (loading || !me) {
    return (
      <div className="flex min-h-[80vh] items-center justify-center bg-[#F7F5F1] pt-20">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#8A8D94]">Loading…</p>
      </div>
    );
  }

  const list = tab === "inquiries" ? inquiries : assessments;

  return (
    <div data-testid="admin-dashboard" className="mx-auto max-w-5xl bg-[#F7F5F1] px-6 pt-36 pb-24 sm:px-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Overline>Private Administration</Overline>
          <h1 className="mt-3 font-serif text-4xl tracking-tight text-[#26282E]">Inquiries &amp; Assessments</h1>
          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#8A8D94]">Signed in as {me.email}</p>
        </div>
        <button
          data-testid="admin-logout-button"
          onClick={logout}
          className="rounded-md border border-[#26282E]/15 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#5B5E66] transition-colors hover:border-[#2F5D8C]/60 hover:text-[#2F5D8C]"
        >
          Sign Out
        </button>
      </div>

      <div className="mt-10 flex flex-wrap gap-2" role="tablist">
        {[
          { key: "inquiries", label: `Consultation Requests (${inquiries.length})` },
          { key: "assessments", label: `Self-Assessments (${assessments.length})` },
        ].map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`rounded-full border px-5 py-3 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${
              tab === t.key ? "border-[#2F5D8C] bg-[#2F5D8C]/10 text-[#2F5D8C]" : "border-[#26282E]/10 text-[#5B5E66] hover:text-[#26282E]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8 space-y-4" data-testid="admin-list">
        {list.length === 0 && (
          <p className="border border-[#26282E]/10 bg-white p-10 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-[#8A8D94]" data-testid="admin-empty-state">
            Nothing here yet.
          </p>
        )}
        {tab === "inquiries" && inquiries.map((i) => <InquiryCard key={i.id} i={i} />)}
        {tab === "assessments" && assessments.map((a) => <AssessmentCard key={a.id} a={a} />)}
      </div>
    </div>
  );
}
